import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Briefcase, MapPin, Clock, Search, Filter } from 'lucide-react';
import { jobsData } from '../../../detail/JobDetailPage/jobsData';

const CareersPage = () => {
	const navigate = useNavigate();
	const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
	const [searchQuery, setSearchQuery] = useState<string>('');

	// Single source of truth — pull directly from jobsData
	const jobs = Object.values(jobsData);

	const departments = [
		{ value: 'all', label: 'All Positions' },
		{ value: 'Engineering', label: 'Engineering' },
		{ value: 'Security', label: 'Security' },
		{ value: 'Product', label: 'Product' },
		{ value: 'Design', label: 'Design' },
		{ value: 'Data', label: 'Data' },
		{ value: 'Operations', label: 'Operations' }
	];

	const filteredJobs = jobs.filter(job => {
		const matchesDepartment = selectedDepartment === 'all' || job.department === selectedDepartment;
		const matchesSearch = job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
			job.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
			job.skills.some(skill => skill.toLowerCase().includes(searchQuery.toLowerCase()));
		return matchesDepartment && matchesSearch;
	});

	return (
		<div className="text-white">
			<div className="py-16 px-6">
				<div className="max-w-7xl mx-auto">
					<div className="text-center mb-12">
						<h2 className="text-4xl font-bold mb-4">Open Positions</h2>
						<p className="text-gray-300 max-w-2xl mx-auto">
							Explore our current openings and find the perfect role for your skills and aspirations.
						</p>
					</div>

					{/* Filters */}
					<div className="mb-8 flex flex-col md:flex-row gap-4">
						<div className="flex-1 relative">
							<Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300" size={20} />
							<input
								type="text"
								placeholder="Search by job title, skills, or keywords..."
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="w-full bg-purple-600/5 border border-purple-500/20 rounded-lg pl-12 pr-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 transition-colors"
							/>
						</div>

						<div className="relative md:w-64">
							<Filter className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-300" size={20} />
							<select
								value={selectedDepartment}
								onChange={(e) => setSelectedDepartment(e.target.value)}
								className="w-full bg-purple-600/5 border border-purple-500/20 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-purple-500/50 transition-colors appearance-none cursor-pointer"
							>
								{departments.map(dept => (
									<option key={dept.value} value={dept.value} className="bg-[#0f1729]">
										{dept.label}
									</option>
								))}
							</select>
						</div>
					</div>

					{/* Results Count */}
					<div className="mb-6 text-gray-300">
						Showing {filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'}
					</div>

					{/* Job Grid */}
					<div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
						{filteredJobs.map((job) => {
							const Icon = job.icon;
							return (
								<div
									key={job.id}
									className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 hover:border-purple-500/50 hover:from-purple-600/10 hover:to-blue-600/10 transition-all duration-300 hover:-translate-y-1"
								>
									<div className="p-6 flex flex-col h-full">
										<div className="flex items-center justify-between mb-4">
											<div className="bg-gradient-to-r from-purple-600 to-blue-600 p-3 rounded-lg">
												<Icon size={24} />
											</div>
											<span className="bg-purple-500/10 px-3 py-1 rounded-full text-sm text-purple-400 border border-purple-500/30">
												{job.department}
											</span>
										</div>

										<h3 className="text-xl font-bold mb-3 hover:text-purple-400 transition-colors">
											{job.title}
										</h3>

										<div className="space-y-2 mb-4">
											<div className="flex items-center gap-2 text-sm text-gray-300">
												<MapPin size={16} />
												<span>{job.location}</span>
											</div>
											<div className="flex items-center gap-2 text-sm text-gray-300">
												<Clock size={16} />
												<span>{job.type} • {job.experience}</span>
											</div>
										</div>

										<p className="text-gray-300 text-sm mb-4 line-clamp-3">
											{job.description}
										</p>

										<div className="flex flex-wrap gap-2 mb-4">
											{job.skills.slice(0, 3).map((skill, index) => (
												<span
													key={index}
													className="bg-purple-500/10 px-3 py-1 rounded-full text-xs text-gray-300 border border-purple-500/20"
												>
													{skill}
												</span>
											))}
											{job.skills.length > 3 && (
												<span className="bg-purple-500/10 px-3 py-1 rounded-full text-xs text-gray-300 border border-purple-500/20">
													+{job.skills.length - 3}
												</span>
											)}
										</div>

										<button
											onClick={() => navigate(`/careers/${job.id}`)}
											className="mt-auto h-[52px] px-12 py-4 rounded-full border-none transition-all bg-gradient-to-r from-indigo-500 to-purple-500 text-white w-full flex items-center justify-center gap-2 group hover:opacity-90"
										>
											Apply Now
										</button>
									</div>
								</div>
							);
						})}
					</div>

					{/* No Results */}
					{filteredJobs.length === 0 && (
						<div className="text-center py-12">
							<div className="rounded-2xl bg-gradient-to-br from-purple-600/5 to-blue-600/5 border border-purple-500/20 p-12">
								<Briefcase size={48} className="mx-auto mb-4 text-gray-500" />
								<h3 className="text-2xl font-bold mb-2">No positions found</h3>
								<p className="text-gray-300 mb-6">
									Try adjusting your search or filter criteria
								</p>
								<button
									onClick={() => {
										setSearchQuery('');
										setSelectedDepartment('all');
									}}
									className="bg-gradient-to-r from-purple-600 to-blue-600 text-white px-6 py-2 rounded-lg transition-colors hover:opacity-90"
								>
									Clear Filters
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default CareersPage;